import { useState, useEffect } from "react";
import { Navigate, useParams, useNavigate, useLocation } from "react-router-dom";
import { Stack, Typography } from "@mui/material";
import { v4, validate } from "uuid";

import authenticationService, { LoginDto } from "../../services/authenticationService";
import useUserContext from "../../hooks/useUserContext";
import AlertExtended from "../../common/components/mui-extended/AlertExtended";
import { LoginOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { PublicRoutesConfig } from "../RoutesConfig";
import { useAxiosLoader } from "../../common/hooks/useAxiosLoader";
import { dateToStringLong, dateToStringShort, handleDates } from "../../common/utils/dateTime";

export default function Login() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { isAuthenticated } = useUserContext();

  const [isAvailableUid] = useState(validate(uid as string));
  const [certificateMetadata, setCertificateMetadata] = useState<any | undefined>();

  const loading = useAxiosLoader(isAvailableUid);
  const isInDevelopmentMode = process.env.NODE_ENV === "development";

  const [model, setModel] = useState<LoginDto | undefined>();

  useEffect(() => {
    if (isAvailableUid) {
      (async () => await login())();
    }
  }, []);

  const login = async () => {
    await authenticationService.login(uid as string).then(({ data: model }) => {
      if (model.isAuthenticated) {
        authenticationService.initLocalStorage(model);
        const returnUrl = (location.state as any)?.from?.pathname;
        navigate(returnUrl ? returnUrl : PublicRoutesConfig.Home.Path, { replace: true });
      } else {
        setModel(model);
      }
    });
  };

  const devLogin = async () => {
    await authenticationService
      .loginDev()
      .then(({ data: model }) => {
        authenticationService.initLocalStorage(model);
        const returnUrl = (location.state as any)?.from?.pathname;
        navigate(returnUrl ? returnUrl : PublicRoutesConfig.Home.Path, { replace: true });
      })
      .catch((error: any) => {
        if (error?.response?.status === 403) {
          handleDates(error?.response?.data);
          setCertificateMetadata(error?.response?.data);
        }
      });
  };

  const renderNewCertificate = () => (
    <Stack spacing={3}>
      <Typography variant="h5">
        Използваният електронен подпис все още не е асоцииран с потребител в Единство.
      </Typography>
      <AlertExtended type="error">
        <Stack spacing={2}>
          <div>
            За да бъдете регистрирани в системата, изпратете от вашия служебен имейл даннитe за електронния Ви подпис на
            адрес support@notary-chamber.bg.
          </div>
        </Stack>
      </AlertExtended>
      <AlertExtended type="info" title="Данни за електронния подпис">
        <Stack spacing={1} sx={{ mt: 2 }}>
          <div>
            Валидност от {dateToStringLong(model?.notBefore)} до {dateToStringLong(model?.notAfter)}.
          </div>
          <div>SHA 1 на публичния ключ: '{model?.thumbprint}'.</div>
        </Stack>
      </AlertExtended>
    </Stack>
  );

  return (
    <>
      {isAuthenticated ? (
        <Navigate to={PublicRoutesConfig.Home.Path} />
      ) : model && !model?.isAuthenticated ? (
        renderNewCertificate()
      ) : (
        <Stack spacing={3}>
          <Typography variant="h5">Вход с електронен подпис</Typography>
          <AlertExtended type="info">
            <Stack spacing={2}>
              <div>
                За идентифициране на потребителите Информационната система използва квалифициран електронен подпис,
                съдържащ информация за Единния граждански номер (ЕГН) на физическото лице.
              </div>
              <div>
                Електронният подпис трябва да е издаден от регистриран за България от Комисията за регулиране на
                съобщенията доставчик на удостоверителни услуги.
              </div>
            </Stack>
          </AlertExtended>
          {certificateMetadata && (
            <AlertExtended type="error">
              <Stack spacing={2}>
                <div>
                  За да бъдете регистрирани в системата, изпратете от вашия служебен имейл даннитe за електронния Ви
                  подпис на адрес support@notary-chamber.bg.
                </div>
                <div>
                  Валидност от {dateToStringShort(certificateMetadata.notBefore)} до{" "}
                  {dateToStringShort(certificateMetadata.notAfter)}.
                </div>
                <div>SHA 1 на публичния ключ: '{certificateMetadata.thumbprint}'.</div>
              </Stack>
            </AlertExtended>
          )}
          <Stack direction="row" justifyContent="center">
            {isInDevelopmentMode && (
              <LoadingButton
                variant="contained"
                loading={loading}
                loadingPosition="start"
                startIcon={<LoginOutlined />}
                size="large"
                onClick={() => devLogin()}
              >
                ВХОД С ЕЛЕКТРОНЕН ПОДПИС / DEV
              </LoadingButton>
            )}
            {!isInDevelopmentMode && (
              <LoadingButton
                variant="contained"
                loading={loading}
                loadingPosition="start"
                startIcon={<LoginOutlined />}
                size="large"
                href={"/auth/index.html?v=" + v4()}
              >
                ВХОД С ЕЛЕКТРОНЕН ПОДПИС
              </LoadingButton>
            )}
          </Stack>
        </Stack>
      )}
    </>
  );
}
